import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type {
  ProjectTeamData,
  ProjectUser,
  InvitedUser,
  User,
  Client,
  DeleteUsersPayload,
  Trade,
  TradePartner,
  TradePartnerApplication,
  TradePartnerApplicationSubmitInput,
  TradePartnerInput,
  ProjectRoleSummary,
} from '../types/types';

export interface TradePartnersResponse {
  trade_partners: TradePartner[];
  trades: Trade[];
  project_roles: ProjectRoleSummary[];
  available_clients: Client[];
}

export interface ApiErrorShape {
  message?: string;
  data?: {
    error?: string;
    detail?: string;
    errors?: Record<string, string[]>;
    [key: string]: unknown;
  };
}

export const getProjectTeamErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const apiError = error as ApiErrorShape;
  return apiError.data?.error || apiError.data?.detail || apiError.message || fallback;
};

export const projectTeamApi = {
  /**
   * Get project team data
   */
  getProjectTeam: async (clientName: string, projectName: string): Promise<ProjectTeamData> => {
    const response = await GET(
      `/base/projects/${encodeURIComponent(clientName)}/${encodeURIComponent(projectName)}/team/`,
    );
    return response.data;
  },

  /**
   * Add existing internal user to project
   */
  addUserToProject: async (data: {
    project_id: number;
    user_id: number;
    client_name?: string;
    role_id: number;
  }): Promise<ProjectUser> => {
    const response = await POST('/base/projects/add-user/', data);
    return response.data;
  },

  /**
   * Invite new internal user to project
   */
  inviteInternalUser: async (data: {
    project_id: number;
    email: string;
    role_id: number;
    client_id?: number;
  }): Promise<{ success: boolean; error?: string }> => {
    const response = await POST('/base/invite-internal-user/', data);
    return response.data;
  },

  /**
   * Invite external user to project
   */
  inviteExternalUser: async (data: {
    project_id: number;
    external_email: string;
    external_company_name: string;
    role_id: number;
    client_name?: string;
  }): Promise<InvitedUser> => {
    const response = await POST('/base/projects/invite-user/', data);
    return response.data;
  },

  /**
   * Delete members from project (ProjectMembership and pending InvitedUser)
   */
  deleteUsers: async (payload: DeleteUsersPayload): Promise<{
    message: string;
    deleted_count: number;
    warnings?: string[];
  }> => {
    const response = await POST('/base/projects/delete-users/', payload);
    return response.data;
  },

  /**
   * Get available users for a client (for adding to project)
   */
  getClientUsers: async (clientId: number, projectId?: number): Promise<{ users: User[] }> => {
    const url = projectId 
      ? `/base/clients/${clientId}/users/?project_id=${projectId}`
      : `/base/clients/${clientId}/users/`;
    const response = await GET(url);
    return response.data;
  },

  /**
   * Get trade partners for a project/client pair
   */
  getTradePartners: async (
    projectId: number,
    clientId?: number,
  ): Promise<TradePartnersResponse> => {
    const url = clientId
      ? `/base/projects/${projectId}/trade-partners/?client_id=${clientId}`
      : `/base/projects/${projectId}/trade-partners/`;
    const response = await GET(url);
    return response.data;
  },

  /**
   * Create a trade partner
   */
  createTradePartner: async (
    projectId: number,
    data: TradePartnerInput,
  ): Promise<TradePartner> => {
    const response = await POST(`/base/projects/${projectId}/trade-partners/`, data);
    return response.data;
  },

  /**
   * Update a trade partner
   */
  updateTradePartner: async (
    projectId: number,
    partnerId: number,
    data: Partial<TradePartnerInput>,
  ): Promise<TradePartner> => {
    const response = await PATCH(`/base/projects/${projectId}/trade-partners/${partnerId}/`, data);
    return response.data;
  },

  /**
   * Delete a trade partner
   */
  deleteTradePartner: async (projectId: number, partnerId: number, clientId?: number): Promise<void> => {
    const suffix = clientId ? `?client_id=${clientId}` : '';
    await DELETE(`/base/projects/${projectId}/trade-partners/${partnerId}/${suffix}`);
  },

  /**
   * Create a trade option for this project's managing client
   */
  createTrade: async (
    projectId: number,
    name: string,
    clientId?: number,
  ): Promise<Trade> => {
    const response = await POST(`/base/projects/${projectId}/trades/`, {
      name,
      client_id: clientId,
    });
    return response.data;
  },

  getTradePartnerApplication: async (
    token: string,
  ): Promise<{ application: TradePartnerApplication }> => {
    const response = await GET(
      `/base/trade-partner-application/?token=${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  submitTradePartnerApplication: async (
    data: TradePartnerApplicationSubmitInput,
  ): Promise<{ message: string; application: TradePartnerApplication }> => {
    const response = await POST('/base/trade-partner-application/', data);
    return response.data;
  },

  resendTradePartnerApplication: async (
    projectId: number,
    applicationId: number,
  ): Promise<{ message?: string; application: TradePartnerApplication }> => {
    const response = await POST(
      `/base/projects/${projectId}/trade-partner-applications/${applicationId}/resend/`,
      {},
    );
    return response.data;
  },

  /**
   * Get invitation details by token
   */
  getInvitationByToken: async (token: string): Promise<{ invited_user: InvitedUser }> => {
    const response = await GET(`/base/accept-invitation/?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  /**
   * Accept invitation by token
   */
  acceptInvitation: async (token: string): Promise<{ message: string; invited_user: InvitedUser }> => {
    const response = await POST('/base/accept-invitation/', { token });
    return response.data;
  },
};
